import React, { useRef, useState, useEffect } from "react";
import { motion, useSpring, AnimatePresence } from "framer-motion";
import { Play } from "lucide-react";

const StudioCamera3D = ({ tilt }) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0, px: 50, py: 50 });
  const [localTilt, setLocalTilt] = useState({ rx: 10, ry: -8 });

  // Smooth springs for mouse tracking reticle
  const springX = useSpring(0, { stiffness: 220, damping: 25 });
  const springY = useSpring(0, { stiffness: 220, damping: 25 });

  useEffect(() => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause();
      }
    }
  }, [playing]);

  // Sync baseline tilt from prop when it changes
  useEffect(() => {
    if (!hovered) {
      setLocalTilt({ rx: tilt.rx, ry: tilt.ry });
    }
  }, [tilt, hovered]);

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const px = (x / rect.width) * 100;
    const py = (y / rect.height) * 100;

    setMousePos({ x, y, px, py });
    springX.set(x);
    springY.set(y);

    // Calculate dynamic 3D tilt tracking: camera aims/turns towards mouse
    const rx = tilt.rx + (0.5 - (y / rect.height)) * 16;
    const ry = tilt.ry + ((x / rect.width) - 0.5) * 22;
    setLocalTilt({ rx, ry });
  };

  const handleMouseEnter = () => {
    setHovered(true);
  };

  const handleMouseLeave = () => {
    setHovered(false);
    // Smoothly spring back to baseline tilt
    setLocalTilt({ rx: tilt.rx, ry: tilt.ry });
  };

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="relative w-full aspect-square cursor-none select-none"
      style={{ perspective: "1400px" }}
    >
      <motion.div
        animate={{ rotateX: localTilt.rx, rotateY: localTilt.ry }}
        transition={{ type: "spring", stiffness: 75, damping: 18 }}
        className="relative w-full h-full"
        style={{ transformStyle: "preserve-3d" }}
      >
        {/* Floor grid */}
        <div
          className="absolute left-1/2 -translate-x-1/2"
          style={{
            bottom: "8%",
            width: "95%",
            height: "55%",
            transform: "translate3d(-50%, 0, 0) rotateX(72deg)",
            transformOrigin: "center bottom",
            backgroundImage:
              "linear-gradient(rgba(228,199,122,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(228,199,122,0.1) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
            maskImage: "radial-gradient(ellipse at center, black 40%, transparent 85%)",
            WebkitMaskImage: "radial-gradient(ellipse at center, black 40%, transparent 85%)"
          }}
        />

        {/* Back wall glow */}
        <div
          className="absolute inset-0 rounded-sm"
          style={{
            transform: "translateZ(-200px)",
            background: "radial-gradient(ellipse at center, rgba(228,199,122,0.15), transparent 60%)"
          }}
        />

        {/* ==================== 3D CINEMA CAMERA MODEL ==================== */}

        {/* TRIPOD RIG & SUPPORT STAND (Z = -50px) */}
        <div
          className="absolute left-1/2 -translate-x-1/2"
          style={{
            bottom: "10%",
            width: "50%",
            height: "25%",
            transform: "translate3d(-50%, 0, -40px)",
            transformStyle: "preserve-3d"
          }}
        >
          <svg viewBox="0 0 200 150" fill="none" className="w-full h-full">
            {/* Tripod mounting plate */}
            <rect x="70" y="0" width="60" height="15" rx="3" fill="#14110f" stroke="rgba(228,199,122,0.3)" strokeWidth="1" />
            <path d="M 85 15 L 115 15 L 120 35 L 80 35 Z" fill="#1c1815" stroke="rgba(228,199,122,0.2)" strokeWidth="1" />
            
            {/* Heavy fluid head knobs & level bubble */}
            <circle cx="85" cy="25" r="4" fill="#c9a961" />
            <circle cx="115" cy="25" r="5" fill="#14110f" stroke="#c9a961" strokeWidth="1" />
            
            {/* Tripod carbon fiber legs */}
            <path d="M 85 35 L 30 150 M 115 35 L 170 150" stroke="#14110f" strokeWidth="6" strokeLinecap="round" />
            <path d="M 85 35 L 30 150 M 115 35 L 170 150" stroke="#1c1815" strokeWidth="4" strokeLinecap="round" />
            <path d="M 100 35 L 100 150" stroke="#0d0b09" strokeWidth="8" />
            <path d="M 100 35 L 100 150" stroke="#1c1815" strokeWidth="5" />
            
            {/* Gold highlights on legs */}
            <rect x="42" y="70" width="4" height="12" fill="#c9a961" rx="1" />
            <rect x="154" y="70" width="4" height="12" fill="#c9a961" rx="1" />
            <rect x="98" y="75" width="4" height="12" fill="#c9a961" rx="1" />
          </svg>
        </div>

        {/* MAIN CAMERA RIG & BODY (Z = 0px) */}
        <div
          className="absolute left-1/2"
          style={{
            bottom: "28%",
            width: "56%",
            height: "42%",
            transform: "translate3d(-52%, 0, 0)",
            transformStyle: "preserve-3d"
          }}
        >
          <svg viewBox="0 0 300 220" fill="none" className="w-full h-full overflow-visible" style={{ filter: "drop-shadow(0 25px 45px rgba(0,0,0,0.65))" }}>
            {/* Camera Cage (Matte Black metal skeleton) */}
            <rect x="70" y="45" width="140" height="110" rx="6" fill="#14110f" stroke="rgba(228,199,122,0.3)" strokeWidth="1.5" />
            
            {/* Top Cheese Plate & Metal mounting points */}
            <rect x="80" y="35" width="120" height="10" rx="2" fill="#1c1815" stroke="#8a7e6d" strokeWidth="0.5" />
            <circle cx="95" cy="40" r="2" fill="#000" />
            <circle cx="115" cy="40" r="2" fill="#000" />
            <circle cx="140" cy="40" r="2" fill="#000" />
            <circle cx="165" cy="40" r="2" fill="#000" />
            <circle cx="185" cy="40" r="2" fill="#000" />

            {/* Premium Gold Accent Rails & Rod Support */}
            <rect x="40" y="148" width="180" height="8" rx="2" fill="#c9a961" />
            <rect x="40" y="149" width="180" height="3" fill="#e4c77a" />
            <circle cx="60" cy="152" r="3.5" fill="#14110f" />
            <circle cx="200" cy="152" r="3.5" fill="#14110f" />

            {/* Heavy Duty Cinema Matte Box / Sun Shade (Z-depth front) */}
            <path d="M 45 40 L 80 55 L 80 135 L 45 150 Z" fill="#0d0b09" stroke="#1c1815" strokeWidth="1" />
            <path d="M 10 20 L 50 42 L 50 148 L 10 170 Z" fill="#14110f" stroke="rgba(228,199,122,0.4)" strokeWidth="1.5" />
            <path d="M 10 20 L 50 42 L 50 46 L 15 26 Z" fill="#c9a961" opacity="0.6" /> {/* Gold trim flare */}
            
            {/* Huge 35mm Cine Lens Barrel (Extends forward) */}
            <rect x="45" y="65" width="35" height="65" fill="#1c1815" stroke="#332c26" strokeWidth="1" />
            {/* Gear teeth/Focus Ring */}
            <line x1="50" y1="65" x2="50" y2="130" stroke="#c9a961" strokeWidth="2" strokeDasharray="2 2" />
            <line x1="68" y1="65" x2="68" y2="130" stroke="#c9a961" strokeWidth="2" strokeDasharray="2 2" />
            <rect x="58" y="63" width="6" height="69" rx="1" fill="#14110f" stroke="#c9a961" strokeWidth="0.5" />

            {/* Red Cine Recording LED (Pulsing Tally Light) */}
            <motion.circle
              cx="135"
              cy="65"
              r="4.5"
              fill="#ff3b30"
              animate={{ opacity: [0.3, 1, 0.3], scale: [0.95, 1.15, 0.95] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            />
            <text x="146" y="68" fill="#8a7e6d" fontFamily="Bebas Neue" fontSize="9" letterSpacing="1">TALLY</text>

            {/* Camera Body venting system / carbon lines */}
            <line x1="95" y1="85" x2="155" y2="85" stroke="rgba(201,169,97,0.15)" strokeWidth="2" />
            <line x1="95" y1="95" x2="155" y2="95" stroke="rgba(201,169,97,0.15)" strokeWidth="2" />
            <line x1="95" y1="105" x2="155" y2="105" stroke="rgba(201,169,97,0.15)" strokeWidth="2" />
            <line x1="95" y1="115" x2="155" y2="115" stroke="rgba(201,169,97,0.15)" strokeWidth="2" />

            {/* Large Cine Battery Mount (V-Mount battery block at back) */}
            <rect x="205" y="55" width="28" height="90" rx="3" fill="#1c1815" stroke="#332c26" strokeWidth="1" />
            <path d="M 215 50 L 225 50 L 228 55 L 212 55 Z" fill="#c9a961" />
            {/* Gold connector pins */}
            <circle cx="212" cy="70" r="1.5" fill="#e4c77a" />
            <circle cx="212" cy="85" r="1.5" fill="#e4c77a" />
            <circle cx="212" cy="100" r="1.5" fill="#e4c77a" />

            {/* Heavy top handle for low-angle shots */}
            <path d="M 130 35 L 120 12 L 195 12 L 185 35" stroke="#14110f" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M 130 35 L 120 12 L 195 12 L 185 35" stroke="#1c1815" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
            <rect x="135" y="10" width="45" height="4" fill="#c9a961" rx="1" />
          </svg>
        </div>

        {/* CAMERA LENS REFLECTION & GLASS APERTURE (Z = 40px) */}
        <div
          className="absolute"
          style={{
            bottom: "40.5%",
            left: "24.5%",
            width: "16%",
            height: "16%",
            transform: "translate3d(0, 0, 40px)",
            transformStyle: "preserve-3d"
          }}
        >
          {/* Futuristic lens iris reflection */}
          <div className="w-full h-full rounded-full bg-gradient-to-tr from-[#0a0908] via-[#14110f] to-[#1c1815] border-2 border-[var(--gold)] flex items-center justify-center relative overflow-hidden shadow-[0_0_30px_rgba(201,169,97,0.4)]">
            {/* Concentric glass elements */}
            <div className="w-4/5 h-4/5 rounded-full border border-dashed border-[rgba(228,199,122,0.3)] flex items-center justify-center">
              <div className="w-2/3 h-2/3 rounded-full bg-gradient-to-bl from-[#b87333]/20 via-transparent to-[#c9a961]/20 flex items-center justify-center">
                <div className="w-1/2 h-1/2 rounded-full bg-[#000] border border-[var(--gold-bright)] opacity-90 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_8px_#22d3ee]" />
                </div>
              </div>
            </div>
            {/* Lens flares */}
            <div
              className="absolute top-1 left-3 w-12 h-6 bg-white/10 rounded-full blur-[1px] rotate-[35deg]"
              style={{ transformOrigin: "top left" }}
            />
            <div className="absolute bottom-2 right-2 w-8 h-4 bg-cyan-500/10 rounded-full blur-[2px] -rotate-[45deg]" />
          </div>
        </div>

        {/* CAMERA SIDE MONITOR PLAYING REAL-TIME CINEMATICS (Z = 80px) */}
        <div
          className="absolute"
          style={{
            bottom: "33%",
            left: "48%",
            width: "44%",
            height: "28%",
            transform: "translate3d(0, 0, 80px)",
            transformStyle: "preserve-3d"
          }}
        >
          {/* Swiveling Cine Monitor Housing */}
          <div
            className="w-full h-full rounded-md p-1 bg-[#14110f] border border-[rgba(228,199,122,0.4)] shadow-[0_15px_35px_rgba(0,0,0,0.7)] flex flex-col relative overflow-hidden cursor-pointer group"
            onClick={() => setPlaying(!playing)}
          >
            {/* Real video / preview area */}
            <div className="flex-1 w-full bg-black rounded-sm overflow-hidden relative">
              {playing ? (
                <iframe
                  className="absolute inset-0 w-full h-full border-0 pointer-events-none"
                  src="https://www.youtube.com/embed/KAnKujVDjf4?si=H6yDYLEKXj0C8k_l&start=1063&autoplay=1&mute=1&controls=0&modestbranding=1&rel=0"
                  title="YouTube video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                ></iframe>
              ) : (
                <>
                  <video
                    ref={videoRef}
                    className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 opacity-60"
                    loop
                    muted
                    playsInline
                    poster="https://img.youtube.com/vi/KAnKujVDjf4/maxresdefault.jpg"
                  >
                    <source src="/videos/studio-edit.mp4" type="video/mp4" />
                  </video>

                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-[1px]">
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      className="w-10 h-10 rounded-full flex items-center justify-center border border-[rgba(228,199,122,0.7)] bg-black/50 shadow-lg"
                    >
                      <Play size={14} className="text-[var(--gold)] fill-[var(--gold)] ml-0.5" />
                    </motion.div>
                  </div>
                </>
              )}

              {/* High-end Camera HUD Overlays */}
              <div className="absolute top-1 left-2 right-2 flex justify-between items-center text-[7px] font-impact tracking-[0.15em] text-[var(--gold)] z-10 select-none pointer-events-none">
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse inline-block" />
                  <span>REC 4K</span>
                </div>
                <span>FPS 23.976</span>
              </div>

              <div className="absolute bottom-1 left-2 right-2 flex justify-between items-center text-[7px] font-impact tracking-[0.1em] text-white/70 z-10 select-none pointer-events-none">
                <span>00:24:12:08</span>
                <span className="text-[var(--gold-bright)]">LUT: CINEMA_GOLD</span>
              </div>

              {/* Audio meter visualization */}
              <div className="absolute right-1 top-1/2 -translate-y-1/2 flex flex-col gap-[2px] pointer-events-none select-none">
                <span className="w-2 h-[2px] bg-green-500" />
                <span className="w-3 h-[2px] bg-green-500" />
                <span className="w-3 h-[2px] bg-green-500 animate-pulse" />
                <span className="w-2 h-[2px] bg-yellow-500" />
                <span className="w-1.5 h-[2px] bg-red-500 opacity-20" />
              </div>
            </div>

            {/* Bottom bezel brand name */}
            <div className="h-4 flex items-center justify-between px-1.5 bg-[#0d0b09]">
              <span className="font-impact text-[7px] tracking-[0.25em] text-[var(--gold)] uppercase">MERAKI MONITOR V5</span>
              <div className="flex items-center gap-1.5 text-[6px] text-[#8a7e6d]">
                <span>100% BATT</span>
                <span className="w-3 h-1.5 border border-[#8a7e6d]/50 rounded-[1px] relative inline-block"><span className="absolute left-[1px] top-[1px] bottom-[1px] right-[1px] bg-green-600" /></span>
              </div>
            </div>
          </div>
        </div>

        {/* HIGH-TECH FLOATING LABELS */}
        <FloatLabel text="cinematic" left="22%" top="22%" z={110} delay={0} />
        <FloatLabel text="focal tracking" left="68%" top="20%" z={120} delay={0.4} accent />
        <FloatLabel text="4k raw" right="10%" top="52%" z={100} delay={0.8} />

      </motion.div>

      {/* ==================== HIGH-TECH 3D TARGET SCANNING & LASER PATH LAYER ==================== */}
      <svg className="absolute inset-0 pointer-events-none z-[150] w-full h-full">
        <AnimatePresence>
          {hovered && (
            <>
              {/* Dynamic glowing autofocus laser vector line connecting Lens to Mouse Pointer */}
              <motion.line
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 0.55 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                x1="32.5%"
                y1="51.5%"
                x2={mousePos.x}
                y2={mousePos.y}
                stroke="url(#laserGradient)"
                strokeWidth="1.5"
                strokeDasharray="4 3"
              />
              
              {/* Pulsing signal glow at the lens focus point */}
              <circle cx="32.5%" cy="51.5%" r="4" fill="var(--gold-bright)" opacity="0.8" />
              <motion.circle
                cx="32.5%"
                cy="51.5%"
                r="10"
                stroke="var(--gold)"
                strokeWidth="1"
                fill="none"
                animate={{ scale: [1, 1.8, 1], opacity: [0.8, 0, 0.8] }}
                transition={{ duration: 2, repeat: Infinity }}
              />

              {/* Pulsing lock indicator at the exact target location (under the mouse) */}
              <circle cx={mousePos.x} cy={mousePos.y} r="3" fill="#22c55e" />
              <motion.circle
                cx={mousePos.x}
                cy={mousePos.y}
                r="8"
                stroke="#22c55e"
                strokeWidth="1"
                fill="none"
                animate={{ scale: [1, 2, 1], opacity: [0.9, 0.1, 0.9] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />

              {/* Gradient definition for futuristic look */}
              <defs>
                <linearGradient id="laserGradient" x1="32.5%" y1="51.5%" x2={mousePos.x} y2={mousePos.y} gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#e4c77a" />
                  <stop offset="100%" stopColor="#22c55e" />
                </linearGradient>
              </defs>
            </>
          )}
        </AnimatePresence>
      </svg>

      {/* ==================== FLOATING Lock-On viewfinder PANEL (OFFSET/ASIDE FROM CURSOR) ==================== */}
      <AnimatePresence>
        {hovered && (
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.85 }}
            transition={{ duration: 0.25 }}
            style={{
              x: springX,
              y: springY,
              translateX: "30px", // Sits elegantly to the right/aside of the cursor
              translateY: "-30px" // Sits elegantly above/aside of the cursor
            }}
            className="absolute top-0 left-0 pointer-events-none z-[200] w-24 h-24 flex items-center justify-center"
          >
            {/* Viewfinder Lock Box Corners */}
            <div className="absolute inset-0">
              {/* Top-Left Bracket */}
              <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[var(--gold-bright)] rounded-tl-[1px]" />
              {/* Top-Right Bracket */}
              <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-[var(--gold-bright)] rounded-tr-[1px]" />
              {/* Bottom-Left Bracket */}
              <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-[var(--gold-bright)] rounded-bl-[1px]" />
              {/* Bottom-Right Bracket */}
              <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[var(--gold-bright)] rounded-br-[1px]" />
            </div>

            {/* Compact rotating focus ring */}
            <div className="w-8 h-8 rounded-full border border-dashed border-[var(--gold)]/40 flex items-center justify-center relative">
              <div className="w-1.5 h-[1px] bg-[var(--gold-bright)] absolute" />
              <div className="h-1.5 w-[1px] bg-[var(--gold-bright)] absolute" />
              
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 rounded-full border border-transparent border-t-[var(--gold-bright)]/30"
              />
            </div>

            {/* Floating telemetry tag aside of target */}
            <div className="absolute top-[-32px] left-[32px] flex flex-col items-start gap-[1px] bg-black/85 px-2 py-1 rounded border border-[rgba(201,169,97,0.3)] backdrop-blur-md shadow-2xl select-none whitespace-nowrap">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] animate-pulse inline-block" />
                <span className="font-impact text-[7px] tracking-[0.25em] text-[#22c55e] uppercase">LOCK ON</span>
              </div>
              <span className="font-mono text-[7px] text-[var(--cream)]">
                FOCAL: {(1.1 + (mousePos.px || 50) * 0.038).toFixed(2)}m
              </span>
              <span className="font-mono text-[6px] text-[var(--muted)]">
                AF-D: {Math.round(mousePos.px)}% / {Math.round(mousePos.py)}%
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FloatLabel = ({ text, left, right, top, z = 50, delay = 0, accent = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: [0, -6, 0] }}
    transition={{ opacity: { duration: 0.7, delay }, y: { duration: 4, repeat: Infinity, ease: "easeInOut", delay } }}
    className="absolute flex items-center gap-1.5 px-3 py-1 rounded-full font-impact text-[9px] tracking-[0.2em] uppercase"
    style={{
      left,
      right,
      top,
      transform: `translateZ(${z}px)`,
      background: accent ? "rgba(228,199,122,0.15)" : "rgba(20,17,15,0.9)",
      border: `1px solid ${accent ? "rgba(228,199,122,0.5)" : "rgba(201,169,97,0.25)"}`,
      color: accent ? "#e4c77a" : "#8a7e6d",
      backdropFilter: "blur(4px)"
    }}
  >
    {text}
  </motion.div>
);

export default StudioCamera3D;
